/**
 * CategoryPlaceholder — CSS-only category-styled placeholder shown in the
 * dish card's image area while the real generated image is in flight.
 *
 * Wave B.1 perf win: cards look "done" the moment menu OCR returns names,
 * instead of showing an empty card with a "generating image..." text note.
 * Each card categorizes by dish name + description keywords → category
 * emoji + tinted gradient. Zero network. Zero asset load. Pure CSS.
 *
 * Falls back to a neutral fork-and-knife category when no keywords match.
 */

interface CategoryDef {
  emoji: string;
  /** Tailwind-class gradient — picks tone-on-brand colors per category. */
  gradient: string;
}

// Keyword → category. First match wins, so order matters (specific before generic).
// Patterns deliberately conservative — if a name doesn't match we use NEUTRAL.
const CATEGORIES: Array<{ test: RegExp; def: CategoryDef }> = [
  { test: /\b(pizza|flatbread|focaccia|calzone|stromboli)\b/i,
    def: { emoji: "🍕", gradient: "from-orange-100 via-red-100 to-amber-50" } },
  { test: /\b(sushi|sashimi|nigiri|maki|temaki|chirashi)\b/i,
    def: { emoji: "🍣", gradient: "from-sky-100 via-blue-50 to-indigo-100" } },
  { test: /\b(ramen|pho|udon|soba|laksa|noodle\s*soup)\b/i,
    def: { emoji: "🍜", gradient: "from-amber-100 via-orange-50 to-yellow-100" } },
  { test: /\b(pasta|spaghetti|penne|rigatoni|linguine|fettuccine|carbonara|bolognese|lasagna|ravioli|gnocchi|tortellini|tagliatelle|orzo|risotto)\b/i,
    def: { emoji: "🍝", gradient: "from-amber-50 via-yellow-100 to-orange-100" } },
  { test: /\b(burger|cheeseburger|slider|smashed|patty)\b/i,
    def: { emoji: "🍔", gradient: "from-amber-100 via-orange-100 to-red-100" } },
  { test: /\b(steak|ribeye|sirloin|tenderloin|filet|filet\s*mignon|entrecote|t-bone|tomahawk|wagyu|brisket)\b/i,
    def: { emoji: "🥩", gradient: "from-red-100 via-rose-100 to-orange-100" } },
  { test: /\b(chicken|wings|nuggets|drumstick|tenders|katsu|schnitzel|parm|parmigiana)\b/i,
    def: { emoji: "🍗", gradient: "from-orange-100 via-yellow-100 to-amber-100" } },
  { test: /\b(salmon|tuna|cod|halibut|sea\s*bass|shrimp|prawn|lobster|crab|scallop|oyster|clam|mussel|octopus|squid|ceviche|tartare|carpaccio|crudo)\b/i,
    def: { emoji: "🦐", gradient: "from-pink-100 via-rose-100 to-orange-50" } },
  { test: /\b(taco|burrito|quesadilla|enchilada|fajita|tostada|chimichanga|elote|nachos|al\s*pastor)\b/i,
    def: { emoji: "🌮", gradient: "from-yellow-100 via-orange-100 to-red-100" } },
  { test: /\b(salad|slaw|greens|caesar|caprese|cobb|bowl|grain\s*bowl|buddha\s*bowl|poke)\b/i,
    def: { emoji: "🥗", gradient: "from-lime-100 via-green-100 to-emerald-100" } },
  { test: /\b(soup|bisque|chowder|gazpacho|broth|consomme|minestrone)\b/i,
    def: { emoji: "🍲", gradient: "from-amber-50 via-orange-100 to-red-100" } },
  { test: /\b(sandwich|sub|hoagie|panini|wrap|reuben|blt|club|melt|toast)\b/i,
    def: { emoji: "🥪", gradient: "from-amber-50 via-yellow-100 to-orange-100" } },
  { test: /\b(dumpling|gyoza|pierogi|wonton|momo|empanada|bao|baozi|xiao\s*long|har\s*gow|siu\s*mai|samosa|spring\s*roll|potsticker)\b/i,
    def: { emoji: "🥟", gradient: "from-yellow-50 via-amber-100 to-orange-100" } },
  { test: /\b(curry|masala|tikka|biryani|tagine|stew|goulash|paella|jambalaya|chili)\b/i,
    def: { emoji: "🍛", gradient: "from-yellow-100 via-orange-100 to-red-100" } },
  { test: /\b(cake|brownie|cheesecake|tiramisu|crumble|tart|pie|pastry|cookie|cinnamon\s*roll|crepe|waffle|donut|doughnut|cannoli|profiterole|eclair|baklava|panna\s*cotta|creme\s*brulee|mousse)\b/i,
    def: { emoji: "🍰", gradient: "from-pink-100 via-rose-100 to-amber-100" } },
  { test: /\b(ice\s*cream|gelato|sorbet|sundae|affogato|kulfi|popsicle|granita)\b/i,
    def: { emoji: "🍦", gradient: "from-pink-50 via-rose-100 to-purple-100" } },
  { test: /\b(chocolate|cocoa|truffle|fondue|ganache)\b/i,
    def: { emoji: "🍫", gradient: "from-amber-100 via-orange-100 to-rose-100" } },
  { test: /\b(eggs|omelette|omelet|benedict|frittata|scrambled|poached|sunny\s*side|breakfast|pancake|french\s*toast)\b/i,
    def: { emoji: "🍳", gradient: "from-yellow-100 via-amber-50 to-orange-100" } },
  { test: /\b(fries|chips|tots|onion\s*rings?|hash\s*browns?|potato\s*wedges?|home\s*fries)\b/i,
    def: { emoji: "🍟", gradient: "from-yellow-100 via-orange-100 to-amber-100" } },
  { test: /\b(bread|baguette|sourdough|brioche|pita|naan|tortilla|crackers|breadsticks)\b/i,
    def: { emoji: "🥖", gradient: "from-amber-100 via-yellow-50 to-orange-50" } },
  { test: /\b(cheese|burrata|mozzarella|halloumi|brie|camembert|cheddar|gouda|feta|charcuterie)\b/i,
    def: { emoji: "🧀", gradient: "from-yellow-100 via-amber-100 to-orange-50" } },
  { test: /\b(vegetables?|veggies?|broccoli|cauliflower|asparagus|brussels?|carrots?|squash|zucchini|eggplant|tomato|cucumber|mushroom)\b/i,
    def: { emoji: "🥦", gradient: "from-green-100 via-lime-100 to-emerald-100" } },
  { test: /\b(hummus|guacamole|tzatziki|baba\s*ganoush|dip|spread|tapenade)\b/i,
    def: { emoji: "🫑", gradient: "from-amber-50 via-yellow-100 to-orange-50" } },
  { test: /\b(wine|red\s*wine|white\s*wine|rosé|rose|champagne|prosecco|cava|sparkling|sauvignon|chardonnay|merlot|pinot|cabernet|malbec|tempranillo|riesling)\b/i,
    def: { emoji: "🍷", gradient: "from-rose-100 via-red-50 to-purple-100" } },
  { test: /\b(beer|lager|ale|ipa|pilsner|stout|porter|hefeweizen)\b/i,
    def: { emoji: "🍺", gradient: "from-yellow-100 via-amber-100 to-orange-100" } },
  { test: /\b(cocktail|martini|negroni|margarita|mojito|spritz|aperol|whiskey|whisky|bourbon|gin|vodka|rum|tequila|mezcal|sangria|punch)\b/i,
    def: { emoji: "🍸", gradient: "from-pink-100 via-rose-50 to-purple-100" } },
  { test: /\b(coffee|espresso|americano|cappuccino|latte|macchiato|mocha|cortado|flat\s*white|drip)\b/i,
    def: { emoji: "☕", gradient: "from-amber-100 via-orange-50 to-yellow-100" } },
  { test: /\b(tea|chai|matcha|herbal|green\s*tea|black\s*tea|oolong)\b/i,
    def: { emoji: "🍵", gradient: "from-green-100 via-emerald-50 to-lime-100" } },
  { test: /\b(juice|lemonade|smoothie|milkshake|shake|frappe|slushie)\b/i,
    def: { emoji: "🥤", gradient: "from-yellow-100 via-orange-50 to-pink-100" } },
  { test: /\b(soda|cola|coke|sprite|fanta|root\s*beer|ginger\s*ale|tonic|seltzer|sparkling\s*water)\b/i,
    def: { emoji: "🥤", gradient: "from-sky-100 via-blue-50 to-cyan-100" } },
];

const NEUTRAL: CategoryDef = {
  emoji: "🍽️",
  gradient: "from-orange-50 via-amber-50 to-rose-50",
};

function pickCategory(name: string, description?: string): CategoryDef {
  const haystack = `${name} ${description || ""}`.toLowerCase();
  for (const { test, def } of CATEGORIES) {
    if (test.test(haystack)) return def;
  }
  return NEUTRAL;
}

export interface CategoryPlaceholderProps {
  name: string;
  description?: string;
  /** Show pulse animation indicating generation is in flight. Defaults to true. */
  loading?: boolean;
  /** Extra class names. */
  className?: string;
}

export function CategoryPlaceholder({
  name,
  description,
  loading = true,
  className = "",
}: CategoryPlaceholderProps) {
  const cat = pickCategory(name, description);
  return (
    <div
      className={`aspect-square relative overflow-hidden bg-gradient-to-br ${cat.gradient} ${className}`}
      role="img"
      aria-label={`${name} placeholder`}
    >
      <div
        className={`absolute inset-0 flex items-center justify-center text-6xl ${loading ? "opacity-60 animate-pulse" : "opacity-90"}`}
        style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.08))" }}
      >
        <span aria-hidden="true">{cat.emoji}</span>
      </div>
      {loading && (
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background:
              "linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.45) 50%, transparent 70%)",
            backgroundSize: "200% 100%",
            animation: "yumz-shimmer 1.8s linear infinite",
          }}
        />
      )}
      <style>{`
        @keyframes yumz-shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
}

export default CategoryPlaceholder;
